import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  type CreateEventFormData,
  EventTypeSection,
  EventDetailsSection,
  AuctionSettingsSection,
} from './create-event-sections';
import {
  LineItemsSection,
  SummarySection,
} from './create-event-items';

export function CreateEventView() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CreateEventFormData>({
    title: '',
    eventType: 'sealed_proposal',
    deliveryZone: '',
    deliverySiteId: '',
    requiredDeliveryDate: '',
    bidDeadline: '',
    auctionStartTime: '',
    auctionEndTime: '',
    startingPrice: '',
    reservePrice: '',
    estimatedValue: '',
    description: '',
    lines: [],
  });

  const isReverseAuction = formData.eventType === 'reverse_auction';

  const handleSubmit = () => {
    if (!formData.title) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.deliveryZone) {
      toast.error('Please select a delivery zone');
      return;
    }
    if (!formData.bidDeadline) {
      toast.error('Please set a bid deadline');
      return;
    }
    if (formData.lines.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }
    if (isReverseAuction && (!formData.auctionStartTime || !formData.auctionEndTime)) {
      toast.error('Please set auction start and end times');
      return;
    }

    toast.success(`Created sourcing event: ${formData.title}`);
    navigate('/sourcing');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sourcing')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h2 className="text-xl font-semibold">Create Sourcing Event</h2>
          <p className="text-muted-foreground">Set up a new reverse auction or sealed proposal</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <EventTypeSection formData={formData} setFormData={setFormData} />
          <EventDetailsSection formData={formData} setFormData={setFormData} />
          {isReverseAuction && (
            <AuctionSettingsSection formData={formData} setFormData={setFormData} />
          )}
          <LineItemsSection formData={formData} setFormData={setFormData} />
        </div>

        <SummarySection
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/sourcing')}
        />
      </div>
    </div>
  );
}
